import { Colxx } from 'components/custom/CustomBootstrap';
import { UserContext } from 'contexts/user/UserContext';
import IntlMessages from 'helpers/IntlMessages';
import { getAuthorizeURI, isWechat, setLoginPayload } from 'helpers/helper';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { Alert, Card, Row } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { NavLink, useLocation } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import SocialLoginOptions from './components/SocialLoginOptions';
import { getSafeForumRecoveryNext } from 'services/forumSessionRecovery';
import { shouldAutoStartWechatLogin } from './authLoopGuards';
import {
  isSafePostLoginRedirect,
  RedirectTarget,
  resolvePostLoginRedirect,
  toRedirectPath,
} from './postLoginRedirect';

const Login = ({ history }: any) => {
  const [loginState, setLoginState] = useState<{ alertId?: string; variant?: string; from?: RedirectTarget }>({});
  const { currentUser, isAuthenticated, loading } = useContext(UserContext);
  const [show, setShow] = useState(false);
  const [redirectFrom, setRedirectFrom] = useState<string>();
  const [isRedirectingToWechat, setIsRedirectingToWechat] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();

  // Capture initial redirect URL and alert on mount
  useEffect(() => {
    const state = history.location?.state || {};
    const params = new URLSearchParams(location.search);
    const isForumSessionRecovery = params.get('forum_session_recovery') === 'true';
    const recoveryNext = getSafeForumRecoveryNext(location.search);
    const resolvedState = isForumSessionRecovery
      ? {
        ...state,
        alertId: state.alertId || 'forum.topic.session-expired',
        variant: state.variant || 'warning',
        from: state.from || recoveryNext,
      }
      : state;
    setLoginState(resolvedState);
    if (resolvedState.alertId || params.get('discourse_sso_recovery') === 'true') {
      setShow(true);
    }
    setRedirectFrom(toRedirectPath(resolvedState.from));
    setIsInitialized(true);
  }, [history, location.search]);

  // Auth redirect
  useEffect(() => {
    if (isAuthenticated && isInitialized) {
      const params = new URLSearchParams(location.search);
      const isSsoFlow = params.get('sso_flow') === 'true';

      // Normalize redirect target to string
      const defaultRedirect = currentUser?.username ? `/user/${currentUser.username}` : '/profile';
      const resolvedRedirect = resolvePostLoginRedirect(location, history, defaultRedirect);
      const safeRedirect = resolvedRedirect === defaultRedirect
        ? redirectFrom || defaultRedirect
        : resolvedRedirect;

      if (isSsoFlow) {
        // If this login was initiated by Discourse SSO,
        // redirect back to the SSO endpoint to complete
        // the handshake using session data
        window.location.href = `/api/sso/discourse`;
      } else {
        // Standard web login. Redirect directly to destination
        window.location.href = isSafePostLoginRedirect(safeRedirect) ? safeRedirect : defaultRedirect;
      }
    }
  }, [currentUser?.username, history, isAuthenticated, isInitialized, location, redirectFrom]);

  // WeChat redirect
  useEffect(() => {
    if (isWechat && !isAuthenticated && !loading && isInitialized) {
      const redirectURL = resolvePostLoginRedirect(location, history);
      if (shouldAutoStartWechatLogin(location.search)) {
        setLoginPayload('social-login');
        setIsRedirectingToWechat(true);
        window.location.href = `${getAuthorizeURI('wechat', redirectURL)}`;
      }
    }
  }, [history, isAuthenticated, isInitialized, loading, location]);

  if (loading || isRedirectingToWechat || isAuthenticated) {
    return <div className="loading" />;
  }

  // If the user is not authenticated, show the login form
  return (
    <div className="modal-dialog-centered mt-2 mb-2">
      <div className="container">
        <Row>
          <Colxx xxs="12" md="10" className="mx-auto my-auto">
            <Card className="auth-card">
              <div className="position-relative image-side d-none d-lg-block">
              </div>
              <div className="form-side">
                <div className="alert">
                  <Alert
                    variant={loginState.variant || 'danger'}
                    show={show}
                    onClose={() => setShow(false)}
                    dismissible
                  >
                    {loginState.alertId
                      ? <IntlMessages id={loginState.alertId} />
                      : <IntlMessages id="user.discourse-sso-recovery" />}
                  </Alert>
                </div>
                <NavLink to="/" className="ml-5 mb-4 white d-flex justify-content-center">
                  <span className="logo-single" />
                </NavLink>
                <SocialLoginOptions isMobileBrowserChecker={isMobile} isWechatChecker={isWechat} />
                <h2 className="mt-5 mb-4">
                  <IntlMessages id="user.login-with-email" />
                </h2>
                <LoginForm />
              </div>
            </Card>
          </Colxx>
        </Row>
      </div>
    </div>
  );
};

Login.propTypes = {
  history: PropTypes.object,
};

export default Login;
